/**
 * One rule explained, as an accordion item: the status (active or planned) and what sales
 * sees (blocked, warning, info) as words on chips, the title, and when opened: what it
 * checks, why, what you see in I2Q, what to do and an example. Managers and admins also
 * see the live mode per company (from `GET /rules`).
 *
 * `RuleAccordion` opens the card named in the address (`#<id>`), scrolls to it and moves
 * the focus to its title, so a "Why?" link lands on the right card.
 */

import {
  Accordion,
  Badge,
  type BadgeTone,
  Card,
  DescriptionList,
  Typography,
} from "@norican/react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";

import { useRuleSet } from "@/lib/api";
import { logger } from "@/lib/logger";
import { entityLabel } from "@/lib/rules";

import {
  type CountryGuide,
  type Explanation,
  findExplanation,
  glossaryRulesOf,
  guideLabel,
  type RuleStatus,
  type SeenTone,
  STATUS_LABEL,
  TONE_LABEL,
  TOPIC_LABEL,
} from "./content";
import { ModeBadge, useCanSeeLiveSettings } from "./live-settings";

const STATUS_TONE: Record<RuleStatus, BadgeTone> = { active: "success", planned: "neutral" };
const SEEN_TONE: Record<SeenTone, BadgeTone> = {
  blocked: "danger",
  warning: "warning",
  info: "info",
};

export function StatusBadge({ status }: { status: RuleStatus }) {
  return (
    <Badge tone={STATUS_TONE[status]} variant="subtle" size="sm" data-status={status}>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

export function ToneBadge({ tone, className }: { tone: SeenTone; className?: string }) {
  // `data-seen`, not `data-tone`: the Badge styles itself from its own `data-tone`, and
  // "blocked" there left the chip without its colour.
  return (
    <Badge tone={SEEN_TONE[tone]} size="sm" data-seen={tone} className={className}>
      {TONE_LABEL[tone]}
    </Badge>
  );
}

/** The live mode of the card's registry rules per company. Managers and admins only. */
function LiveModes({ rule, entities }: { rule: Explanation; entities?: readonly string[] }) {
  const canSee = useCanSeeLiveSettings();
  const wanted = canSee && rule.registryIds.length > 0;
  const ruleSet = useRuleSet(wanted);
  if (!wanted) {
    return null;
  }
  const views = (ruleSet.data?.entities ?? []).filter(
    (view) => !entities || entities.includes(view.entity),
  );
  return (
    <section
      className="border-border-subtle flex flex-col gap-2 rounded-md border p-3"
      aria-label="Live setting per company"
      data-live-modes={rule.id}
    >
      <Typography variant="label">Live setting per company (managers and admins)</Typography>
      {ruleSet.isLoading ? (
        <Typography variant="caption" tone="muted">
          Loading the live settings.
        </Typography>
      ) : ruleSet.isError || views.length === 0 ? (
        <Typography variant="caption" tone="muted">
          I2Q cannot show the live settings now. The tab &quot;Live settings&quot; has all of them.
        </Typography>
      ) : (
        <ul className="flex flex-col gap-2">
          {rule.registryIds.map((ruleId) => {
            const title = views
              .flatMap((view) => view.rules ?? [])
              .find((row) => row.rule_id === ruleId)?.title;
            return (
              <li key={ruleId} className="flex min-w-0 flex-col gap-1">
                <span className="text-fg text-xs break-words">
                  {title ?? ruleId}{" "}
                  <span className="text-fg-subtle font-mono break-all">{ruleId}</span>
                </span>
                <span className="flex flex-wrap gap-x-3 gap-y-1">
                  {views.map((view) => {
                    const row = (view.rules ?? []).find((entry) => entry.rule_id === ruleId);
                    return (
                      <span key={view.entity} className="inline-flex items-center gap-1 text-xs">
                        <span className="text-fg-muted">
                          {entityLabel(view.entity, view.label)}
                        </span>
                        {row ? (
                          <ModeBadge mode={row.mode} />
                        ) : (
                          <span className="text-fg-subtle">-</span>
                        )}
                      </span>
                    );
                  })}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export interface RuleCardProps {
  rule: Explanation;
  /** The guide the card is shown on (null for the rules for every country). */
  guide: CountryGuide | null;
  /** Show the guide name on the card (glossary search results). */
  showGuide?: boolean;
  /** Open: the short line under the title is left out, "What it checks" says it in full. */
  open?: boolean;
}

export function RuleCard({ rule, guide, showGuide = false, open = false }: RuleCardProps) {
  const general = glossaryRulesOf(rule);
  return (
    <Accordion.Item value={rule.id} id={rule.id} className="scroll-mt-24" data-rule-card={rule.id}>
      <Accordion.Header>
        <Accordion.Trigger className="text-left">
          <span className="flex min-w-0 flex-col gap-1 py-0.5">
            <span className="flex flex-wrap items-center gap-1.5">
              <StatusBadge status={rule.status} />
              <ToneBadge tone={rule.tone} />
              {showGuide && guide && (
                <Badge tone="accent" variant="outline" size="sm">
                  {guideLabel(guide)}
                </Badge>
              )}
            </span>
            <span className="text-fg text-sm font-semibold break-words">{rule.title}</span>
            {!open && (
              <span className="text-fg-muted line-clamp-2 text-xs font-normal break-words">
                {rule.checks}
              </span>
            )}
          </span>
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Panel>
        <div className="flex max-w-prose min-w-0 flex-col gap-3 pt-2 pb-3">
          <DescriptionList
            orientation="vertical"
            items={[
              {
                label: "What it checks",
                value: <span className="break-words">{rule.checks}</span>,
              },
              { label: "Why", value: <span className="break-words">{rule.why}</span> },
              {
                label: "What you see in I2Q",
                value: <span className="break-words">{rule.youSee}</span>,
              },
              { label: "What to do", value: <span className="break-words">{rule.whatToDo}</span> },
            ]}
          />
          <Card variant="subtle" padding="sm">
            <Typography variant="label">Example</Typography>
            <Typography variant="body-sm" className="break-words">
              {rule.example}
            </Typography>
          </Card>
          {rule.plannedPart && (
            <Typography variant="body-sm" tone="muted" className="break-words">
              Coming later: {rule.plannedPart}
            </Typography>
          )}
          <Typography variant="caption" tone="subtle">
            Topic: {TOPIC_LABEL[rule.topic]}
          </Typography>
          {general.length > 0 && (
            <Typography variant="body-sm" className="break-words">
              Rule for every country:{" "}
              {general.map((entry, index) => (
                <span key={entry.id}>
                  {index > 0 && ", "}
                  <Link
                    to={`/rules/glossary#${entry.id}`}
                    className="text-accent-fg-subtle font-semibold hover:underline"
                  >
                    {entry.title}
                  </Link>
                </span>
              ))}
            </Typography>
          )}
          <LiveModes rule={rule} entities={guide?.entities} />
        </div>
      </Accordion.Panel>
    </Accordion.Item>
  );
}

/** The card id the address points to, when it is one of `rules` (a former id works too). */
function anchorOf(hash: string, rules: readonly Explanation[]): string | null {
  const raw = decodeURIComponent(hash.replace(/^#/, ""));
  if (!raw) {
    return null;
  }
  const located = findExplanation(raw);
  const id = located?.rule.id ?? raw;
  return rules.some((rule) => rule.id === id) ? id : null;
}

export interface RuleAccordionProps {
  rules: readonly Explanation[];
  guide: CountryGuide | null;
  /** Per-card guide (glossary results across guides); falls back to `guide`. */
  guideOf?: (rule: Explanation) => CountryGuide | null;
  showGuide?: boolean;
  label: string;
}

export function RuleAccordion({ rules, guide, guideOf, showGuide, label }: RuleAccordionProps) {
  const { hash } = useLocation();
  const target = anchorOf(hash, rules);
  const [open, setOpen] = useState<string[]>(() => (target ? [target] : []));
  const [seenHash, setSeenHash] = useState(hash);
  // A new "#id" in the address (another "Why?" link, a link on this page) opens that card.
  if (hash !== seenHash) {
    setSeenHash(hash);
    if (target && !open.includes(target)) {
      setOpen([...open, target]);
    }
  }

  // Scroll to the opened card and give its title the focus (syncs with the browser).
  useEffect(() => {
    if (!target) {
      return;
    }
    logger.debug("[rules] open card", target);
    const frame = globalThis.requestAnimationFrame?.(() => {
      document.getElementById(target)?.scrollIntoView({ block: "start" });
      document
        .getElementById(target)
        ?.querySelector<HTMLButtonElement>("button")
        ?.focus({ preventScroll: true });
    });
    return () => {
      if (frame !== undefined) {
        globalThis.cancelAnimationFrame?.(frame);
      }
    };
  }, [target]);

  return (
    <Accordion.Root
      multiple
      value={open}
      onValueChange={(value) => setOpen((value as unknown[]).map(String))}
      hiddenUntilFound
      aria-label={label}
      className="min-w-0"
    >
      {rules.map((rule) => (
        <RuleCard
          key={rule.id}
          rule={rule}
          guide={guideOf ? guideOf(rule) : guide}
          showGuide={showGuide}
          open={open.includes(rule.id)}
        />
      ))}
    </Accordion.Root>
  );
}
